import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/react';
import {
  Truck, Boxes, Tag, LineChart, ShoppingBag,
  Package, Database, Headphones, Megaphone, AlertCircle, UserCircle2,
  Briefcase, Sparkles, Cloud, Wrench, Globe, Cog, BookOpen,
  CreditCard, FileText,
  Landmark, Percent, Radar, FileCheck, PackageOpen, ScanBarcode, Undo2,
  Receipt, Rss,
} from 'lucide-react';
import api from '../utils/api';

/* Centralized app catalogue · fetched from /api/apps/ on mount and
 * exposed to every component that used to read the static array.
 * Includes the icon-name → lucide-component resolver so callers stay
 * agnostic of the icon library.
 *
 * There is intentionally no hard-coded fallback list. A prior version
 * shipped one to "keep the menu populated during deploys", but it
 * silently masked real API failures: stale URLs and missing
 * descriptions kept rendering when /api/apps/ failed or returned an
 * unexpected shape, leaving admin edits invisible. Now we surface the
 * real state · empty until the API resolves. */

/* Cody's fox (a picture rather than a lucide glyph) · same props as an icon. */
const CodyFox = ({ className, style }) => (
  <img src="/cody-assets/icon.png" alt="" className={className} style={{ ...style, borderRadius: '22%' }} />
);

const ICONS = {
  CodyFox,
  Truck, Boxes, Tag, LineChart, ShoppingBag,
  Package, Database, Headphones, Megaphone, AlertCircle, UserCircle2,
  Briefcase, Sparkles, Cloud, Wrench, Globe, Cog, BookOpen,
  CreditCard, FileText,
  Landmark, Percent, Radar, FileCheck, PackageOpen, ScanBarcode, Undo2,
  Receipt, Rss,
};

// eslint-disable-next-line react-refresh/only-export-components
export function iconFor(name) {
  return ICONS[name] || Briefcase;
}

// eslint-disable-next-line react-refresh/only-export-components
export const GROUP_ORDER = ['products', 'purchase', 'supply', 'commerce', 'pricing', 'care', 'accounting', 'internal', 'other'];

/* LampTwist palette · stable, reused by chrome and sidebars.
 * `yellow` matches the lightbulb halo in the logo · used as the
 * brand accent color (CTAs, section markers). `yellowLight` is the
 * soft chip tint. `greenSoft` is the branded background companion. */
// eslint-disable-next-line react-refresh/only-export-components
export const LT = {
  greenDeep:   '#2B4138',
  greenMid:    '#7C9890',
  greenSoft:   '#E6ECE7',
  green30:     '#BFC6C3',
  green50:     '#95A09B',
  beigeWarm:   '#EDECE5',
  beigeLight:  '#FBFBFA',
  yellow:      '#FFBD09',  // brand lamp yellow · accent only, never a button
  yellowLight: '#FFF6D9',
  cta:         '#469E7A',  // brand action green · save, send, add
  ctaHover:    '#3A8767',
};

/* Normalize a server App row to the shape the UI components expect.
 * Keeps the existing `internal` boolean / `to` / `href` fields working
 * for code paths that haven't been rewritten yet. */
function adapt(row) {
  return {
    ...row,
    Icon: iconFor(row.icon_name),
    dot: row.dot_color,
    tint: row.tint_color,
    to: row.internal_route || null,
    href: row.href || null,
    internal: !!row.internal_route,
    group: row.group,
  };
}

const Ctx = createContext({
  apps: [],
  loading: true,
  refresh: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAppCatalogue = () => useContext(Ctx);

export default function AppCatalogueProvider({ children }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Gate the catalogue fetch on a live Clerk session. Provider effects
  // fire bottom-up, so without this we'd call /api/apps/ *before*
  // AuthProvider has set the session on axios · the request would go
  // out with no Authorization header and the backend would 403 us.
  // Other API calls dodge this because they fire from page components
  // that mount after auth is wired.
  const { session, isLoaded: sessionLoaded } = useSession();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.apps();
      // Trust whatever the server returns · including an empty list,
      // which is a real (if odd) state worth surfacing rather than
      // hiding behind stale hard-coded data.
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      // Surface failures · keeping cards visually populated with fake
      // data here would only hide the actual problem.
      console.error('[apps] failed to load catalogue:', e);
      setError(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for Clerk to report its loading state. Once loaded, fetch
    // only if we have a session · signed-out users have nothing to see.
    if (!sessionLoaded) return;
    if (!session) {
      setLoading(false);
      setRows([]);
      return;
    }
    refresh();
  }, [sessionLoaded, session, refresh]);

  const apps = useMemo(() => rows.map(adapt), [rows]);

  return (
    <Ctx.Provider value={{ apps, loading, error, refresh }}>
      {children}
    </Ctx.Provider>
  );
}
